import { useAuth0 } from '@auth0/auth0-react'
import { useEffect, useState } from 'react'
import { Loader, Banner, Breadcrumbs, Table, LayerCard, Empty } from '@cloudflare/kumo'
import { ClipboardText, House } from '@phosphor-icons/react'
import { apiRequest, ApiError } from '../api/client'
import { useTranslation } from '../i18n'
import { PageHeader } from '../components/kumo/page-header/page-header'
import type { WifiAccessLog } from '@hub-wifi/shared'

export function AuditLogPage() {
  const { getAccessTokenSilently } = useAuth0()
  const { t, locale } = useTranslation()
  const [logs, setLogs] = useState<WifiAccessLog[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadLogs() {
      try {
        const token = await getAccessTokenSilently()
        const data = await apiRequest<WifiAccessLog[]>('/api/wifi/logs?limit=500', { token })
        setLogs(data)
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'GENERIC_ERROR')
      } finally {
        setLoading(false)
      }
    }
    void loadLogs()
  }, [getAccessTokenSilently])

  return (
    <div>
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<House size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Current>{t('auditLog.title')}</Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={t('auditLog.title')}
        description={t('auditLog.description')}
      />

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader size="lg" />
        </div>
      )}

      {!loading && error && (
        <Banner variant="error">
          {t('common.errorPrefix')} {error === 'GENERIC_ERROR' ? t('common.error') : error}
        </Banner>
      )}

      {!loading && !error && logs.length === 0 && (
        <Empty
          icon={<ClipboardText size={48} className="text-kumo-inactive" />}
          title={t('auditLog.empty')}
        />
      )}

      {!loading && !error && logs.length > 0 && (
        <LayerCard>
          <LayerCard.Primary className="p-0">
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.Head>{t('auditLog.timestamp')}</Table.Head>
                  <Table.Head>{t('auditLog.action')}</Table.Head>
                  <Table.Head>{t('auditLog.performedBy')}</Table.Head>
                  <Table.Head>{t('auditLog.coworkerId')}</Table.Head>
                  <Table.Head>{t('auditLog.metadata')}</Table.Head>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {logs.map((log) => (
                  <Table.Row key={log.id}>
                    <Table.Cell>{new Date(log.timestamp).toLocaleString(locale)}</Table.Cell>
                    <Table.Cell>{log.action}</Table.Cell>
                    <Table.Cell>{log.performedBy}</Table.Cell>
                    <Table.Cell>{log.coworkerId}</Table.Cell>
                    <Table.Cell>
                      <code className="text-xs bg-kumo-recessed px-1 py-0.5 rounded">
                        {JSON.stringify(log.metadata)}
                      </code>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </LayerCard.Primary>
        </LayerCard>
      )}
    </div>
  )
}
